<template>
  <div class="logs-panel">
    <div class="logs-toolbar">
      <div class="filter-group">
        <label>狀態</label>
        <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
          <option value="">全部</option>
          <option value="success">成功</option>
          <option value="partial">部分成功</option>
          <option value="failed">失敗</option>
          <option value="skipped">略過</option>
        </select>
      </div>
      <button type="button" class="btn btn-outline-primary btn-sm" :disabled="loading" @click="load">
        <i class="ri-refresh-line"></i>
        重新整理
      </button>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入參加紀錄...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button type="button" class="btn btn-secondary btn-sm" @click="load">重試</button>
    </div>

    <template v-else>
      <div class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>參加時間</th>
              <th>會員</th>
              <th>LINE UID</th>
              <th>執行結果</th>
              <th>動作摘要</th>
              <th>錯誤訊息</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="logs.length === 0">
              <td colspan="6" class="empty-state">尚無參加紀錄</td>
            </tr>
            <tr v-for="item in logs" :key="item.id">
              <td>{{ formatDate(item.createdAt) }}</td>
              <td>
                <router-link
                  v-if="item.memberId"
                  :to="{ name: 'ClientMemberDetail', params: { id: item.memberId } }"
                  class="member-link"
                >
                  {{ item.memberName || item.memberCode || item.memberId }}
                </router-link>
                <span v-else class="text-muted">未綁定會員</span>
              </td>
              <td class="mono">{{ truncate(item.lineUserId) }}</td>
              <td>
                <span :class="statusBadgeClass(item.status)">{{ statusLabel(item.status) }}</span>
              </td>
              <td class="action-summary">{{ formatActionSummary(item.actionResults) }}</td>
              <td class="error-cell">{{ item.errorMessage || '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <ListPagination
        v-if="pagination.totalPages > 1"
        :page="currentPage"
        :total-pages="pagination.totalPages"
        :total="pagination.totalItems"
        @change="goToPage"
      />
    </template>
  </div>
</template>

<script>
import { ref, reactive, watch, onMounted } from 'vue';
import { followMarketingCampaignService } from '@/api/modules/followMarketingCampaigns.js';

const ACTION_TYPE_LABELS = {
  points: '點數',
  coupon: '優惠券',
  lottery: '抽獎連結',
  message: '推播',
  limits: '限制',
  none: '無動作'
};

const ACTION_RESULT_LABELS = {
  success: '成功',
  failed: '失敗',
  skipped: '略過'
};

const SKIP_REASON_LABELS = {
  no_member: '未綁定會員',
  one_per_user: '每人限一次',
  total_limit: '達總上限',
  no_actions: '無動作',
  invalid_amount: '點數無效',
  missing_config: '缺少設定',
  template_not_found: '範本不存在',
  template_inactive: '範本未啟用',
  empty_template: '範本為空',
  empty: '內容為空',
  unknown_type: '未知類型'
};

export default {
  name: 'FollowMarketingLogsPanel',
  props: {
    campaignId: { type: String, required: true }
  },
  setup(props) {
    const loading = ref(false);
    const error = ref(null);
    const logs = ref([]);
    const filterStatus = ref('');
    const currentPage = ref(1);
    const pageSize = 20;
    const pagination = reactive({
      totalItems: 0,
      totalPages: 1
    });

    const load = async () => {
      if (!props.campaignId) return;
      loading.value = true;
      error.value = null;
      try {
        const params = {
          page: currentPage.value,
          pageSize
        };
        if (filterStatus.value) params.status = filterStatus.value;

        const res = await followMarketingCampaignService.getLogs(props.campaignId, params);
        const data = res.data?.data ?? res.data;
        logs.value = data?.items || [];
        pagination.totalItems = data?.pagination?.totalItems || 0;
        pagination.totalPages = data?.pagination?.totalPages || 1;
      } catch (e) {
        error.value = e.response?.data?.message || e.message || '載入參加紀錄失敗';
        logs.value = [];
      } finally {
        loading.value = false;
      }
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      load();
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= pagination.totalPages) {
        currentPage.value = page;
        load();
      }
    };

    const statusLabel = (status) => ({
      success: '成功',
      partial: '部分成功',
      failed: '失敗',
      skipped: '略過'
    }[status] || status || '-');

    const statusBadgeClass = (status) => ({
      success: 'badge badge-success',
      partial: 'badge badge-warning',
      failed: 'badge badge-danger',
      skipped: 'badge badge-secondary'
    }[status] || 'badge badge-secondary');

    const formatDate = (value) => {
      if (!value) return '-';
      return new Date(value).toLocaleString('zh-TW');
    };

    const truncate = (value) => {
      if (!value) return '-';
      if (value.length <= 16) return value;
      return `${value.slice(0, 8)}...${value.slice(-6)}`;
    };

    const formatActionSummary = (actionResults) => {
      if (!Array.isArray(actionResults) || actionResults.length === 0) return '-';
      return actionResults
        .map((item) => {
          const typeLabel = ACTION_TYPE_LABELS[item.type] || item.type || '動作';
          const resultLabel = ACTION_RESULT_LABELS[item.result] || item.result || '';
          const reasonLabel = item.reason ? SKIP_REASON_LABELS[item.reason] || item.reason : '';
          if (reasonLabel) return `${typeLabel}：${resultLabel}（${reasonLabel}）`;
          return `${typeLabel}：${resultLabel}`;
        })
        .join('；');
    };

    watch(
      () => props.campaignId,
      () => {
        currentPage.value = 1;
        load();
      }
    );

    onMounted(() => load());

    return {
      loading,
      error,
      logs,
      filterStatus,
      currentPage,
      pagination,
      load,
      handleFilterChange,
      goToPage,
      statusLabel,
      statusBadgeClass,
      formatDate,
      truncate,
      formatActionSummary
    };
  }
};
</script>

<style scoped>
.logs-panel {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px;
}

.logs-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 16px;
}

.filter-group label {
  display: block;
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 4px;
}

.filter-group .form-control {
  min-width: 140px;
}

.table-wrapper {
  overflow-x: auto;
}

.table-wrapper table {
  width: 100%;
  min-width: 900px;
  border-collapse: collapse;
}

.table-wrapper th,
.table-wrapper td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #eee;
  font-size: 14px;
  vertical-align: top;
}

.table-wrapper th {
  background: #f8f9fa;
  font-weight: 600;
}

.mono {
  font-family: monospace;
  font-size: 12px;
}

.member-link {
  color: #2563eb;
  text-decoration: none;
}

.member-link:hover {
  text-decoration: underline;
}

.action-summary {
  max-width: 280px;
  line-height: 1.5;
}

.error-cell {
  max-width: 200px;
  word-break: break-word;
  color: #b91c1c;
}

.empty-state {
  text-align: center;
  color: #6c757d;
  padding: 24px;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
}

.page-info {
  font-size: 13px;
  color: #6c757d;
}

.text-muted {
  color: #adb5bd;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 32px 16px;
}
</style>
